import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useCreateUnitActivation, useGetUnitActivationById, useUpdateUnitActivation } from '@apps-common/apis/portal/unit-activations'
import { UnitActivationPlanForm } from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/components'
import {
  ModifyUnitActivationStep,
  stepDirection
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/Index'
import {
  UnitActivationConfirmationForm,
  UnitActivationUnitForm
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/replace-activation'
import { VertSpacerXl } from '@apps-common/components/layout'
import { useGarbageCollector } from '@apps-common/contexts/GarbageContext'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { IActivationServiceRequestCreateModel } from '@apps-common/types/portal/service-request'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'

import { afterActivationUpdate } from '../helpers/afterActivationUpdate'

interface IProps {
  unitActivationId?: number | null
  defaultUnit?: IUnitGenericDetailsModel
}

export const UnitActivationReplaceModifyView: React.FC<IProps> = ({ unitActivationId, defaultUnit }) => {
  const [step, setStep] = useState<ModifyUnitActivationStep>(ModifyUnitActivationStep.unit)
  const { navigateReplace } = useNavigation()
  const { purge } = useGarbageCollector()

  const isEditing = !Number.isNaN(unitActivationId)

  const moveStep = (direction: stepDirection) => {
    if (direction === 'forward') {
      if (step === ModifyUnitActivationStep.unit) {
        setStep(ModifyUnitActivationStep.plan)

        return ModifyUnitActivationStep.plan
      }
      if (step === ModifyUnitActivationStep.plan) {
        setStep(ModifyUnitActivationStep.confirm)

        return ModifyUnitActivationStep.confirm
      }
      if (step === ModifyUnitActivationStep.confirm) {
        return ModifyUnitActivationStep.submit
      }
    } else if (direction === 'backward') {
      if (step === ModifyUnitActivationStep.confirm) {
        setStep(ModifyUnitActivationStep.plan)

        return ModifyUnitActivationStep.plan
      }
      if (step === ModifyUnitActivationStep.plan) {
        setStep(ModifyUnitActivationStep.unit)

        return ModifyUnitActivationStep.unit
      }
    }

    return step
  }

  const {
    isLoading: activationIsLoading,
    isError: activationIsError,
    data: activation
  } = useGetUnitActivationById({
    config: { enabled: isEditing },
    id: unitActivationId || 0
  })

  const { mutate: createActivation, isPending: createActivationIsPending } = useCreateUnitActivation(true, (data) =>
    afterActivationUpdate(data, navigateReplace, purge)
  )
  const { mutate: updateActivation, isPending: updateActivationIsPending } = useUpdateUnitActivation(true, (data) =>
    afterActivationUpdate(data, navigateReplace, purge)
  )

  const methods = useForm<IActivationServiceRequestCreateModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })
  const { handleSubmit, reset } = methods

  useEffect(() => {
    if (activation) {
      reset(activation)
    }
  }, [activation])

  const onSubmit = async (values: IActivationServiceRequestCreateModel) => {
    const nextStep = moveStep('forward')

    if (nextStep !== ModifyUnitActivationStep.submit) return

    if (!isEditing) {
      createActivation({ model: values })
    } else {
      updateActivation({ model: values })
    }
  }

  const loading = isEditing && (activationIsLoading || activationIsError)
  const updating = createActivationIsPending || updateActivationIsPending

  return (
    <div className='max-w-[800px]'>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          {/* step 1 - unit */}
          <UnitActivationUnitForm loading={loading} updating={updating} step={step} moveStep={moveStep} defaultUnit={defaultUnit} />
          <VertSpacerXl />
          {/* step 2 - plan */}
          <UnitActivationPlanForm stepNo={2} loading={loading} updating={updating} step={step} moveStep={moveStep} />
          <VertSpacerXl />
          {/* step 3 - confirm */}
          <UnitActivationConfirmationForm loading={loading} updating={updating} step={step} moveStep={moveStep} />
        </form>
      </FormProvider>
      <VertSpacerXl />
    </div>
  )
}
